import { useState } from 'react';
import { Link } from 'react-router';
import { clsx } from 'clsx';
import { SEO, BreadcrumbSchema, FAQSchema } from '@/components/seo/seo';
import { CouncilPackProof } from '@/components/claims/council-pack-proof';

const comparisonData = [
  {
    feature: 'Eligibility assessment',
    description: 'Quick checks to see whether your incident appears eligible and which authority is responsible.',
    free: true,
    council: true,
    court: true,
  },
  {
    feature: 'Professional claim letter',
    description: 'Professionally structured letter template referencing the relevant law, ready for council submission.',
    free: false,
    council: true,
    court: true,
  },
  {
    feature: 'Evidence bundle organisation',
    description: 'Your photos, documents, and details organised clearly into a single, coherent claim pack.',
    free: false,
    council: true,
    court: true,
  },
  {
    feature: 'AI-powered evidence analysis',
    description: 'Automated checks to flag missing, unclear, or inconsistent evidence before you submit.',
    free: false,
    council: true,
    court: true,
  },
  {
    feature: 'Prior reports check (FixMyStreet)',
    description: 'Checks whether the pothole was previously reported, which can be relevant to your claim.',
    free: false,
    council: true,
    court: true,
  },
  {
    feature: 'Council-specific submission guide',
    description: 'Step-by-step guidance showing where and how to submit your claim to your council.',
    free: false,
    council: true,
    court: true,
  },
  {
    feature: 'Know Your Rights briefing',
    description: 'General information explaining relevant duties, responsibilities, and the claims process.',
    free: false,
    council: true,
    court: true,
  },
  {
    feature: 'Pre-filled N1 court form',
    description: 'An N1 claim form prepared using your information, ready for review and submission.',
    free: false,
    council: false,
    court: true,
  },
  {
    feature: 'Particulars of claim template',
    description: 'A structured template to help you set out your claim clearly for the court.',
    free: false,
    council: false,
    court: true,
  },
  {
    feature: 'Court-ready evidence bundle',
    description: 'Your evidence organised in a format suitable for small claims court submission.',
    free: false,
    council: false,
    court: true,
  },
  {
    feature: 'Hearing preparation guide',
    description: 'Plain-English guidance on what to expect from the small claims hearing process.',
    free: false,
    council: false,
    court: true,
  },
];

const valueProps = [
  {
    icon: '£',
    title: '£35 flat fee',
    description: 'A one-off purchase with no subscription or recurring charge.',
  },
  {
    icon: '📄',
    title: 'Prepared documents',
    description: 'Fixtyer organises your information and evidence into a claim pack for you to review.',
  },
  {
    icon: '🚗',
    title: 'You manage the claim',
    description: 'You review, submit, and manage your claim directly with the responsible authority.',
  },
  {
    icon: '🔎',
    title: 'Relevant public context',
    description: 'Prior reports and DfT ratings can add context, but do not determine liability or outcomes.',
  },
];

const alternatives = [
  {
    option: 'Do it yourself',
    cost: 'Free',
    time: '5-10 hours',
    issues: [
      'Research council processes yourself',
      'Figure out which authority is responsible',
      'Structure your own claim letter',
      'Risk citing the wrong laws',
      'No prior reports check',
      'Increased rejection risk from poor formatting',
    ],
  },
  {
    option: 'Claims management company',
    cost: '25-35% of payout',
    time: 'Weeks of waiting',
    issues: [
      'They take a big cut of your money',
      'You lose control of your claim',
      'Often use generic templates',
      'May not check prior reports',
      'No transparency on progress',
      'Pushy sales tactics',
    ],
  },
  {
    option: 'Solicitor',
    cost: '£200-500+',
    time: 'Weeks',
    issues: [
      'Expensive for small claims',
      'Often won\'t take pothole cases',
      'Overkill for council submissions',
      'You still need to provide evidence',
      'Legal costs may exceed the claim value',
      'Usually unnecessary',
    ],
  },
];

const pricingFaqs = [
  {
    question: 'When do I pay?',
    answer: 'You only pay when you\'re ready to generate your claim pack. The eligibility check, evidence upload, and guidance are all free. You can see exactly what your pack contains before purchasing.',
  },
  {
    question: 'What if my claim is rejected?',
    answer: 'You remain responsible for deciding what to do after a rejection, including whether to seek legal advice. An £89 Court Pack is planned, but it is not currently available to purchase.',
  },
  {
    question: 'Do you take a percentage of my compensation?',
    answer: 'Absolutely not. You pay a flat fee and keep 100% of any compensation you may receive. That\'s the whole point — no middlemen taking a cut.',
  },
  {
    question: 'Is there a money-back guarantee?',
    answer: 'We can\'t guarantee your claim will succeed — that depends on the strength of your evidence and the council\'s response. But we guarantee your documents will be professionally structured and legally referenced. If there\'s a technical issue with your pack, we\'ll fix it.',
  },
  {
    question: 'Can I use the pack for multiple claims?',
    answer: 'Each pack is generated for a specific incident. If you have multiple pothole incidents, you\'ll need a separate pack for each — but you can use the same account and only pay for the packs you generate.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit and debit cards through our secure payment processor. Your payment details are never stored on our servers.',
  },
];

export function PricingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Pricing', url: '/pricing' },
  ];

  const faqsForSchema = pricingFaqs.map(f => ({
    question: f.question,
    answer: f.answer,
  }));

  return (
    <div className="relative">
      <SEO
        title="Pricing | Simple, Transparent Pricing | Fixtyer"
        description="The Council Claim Pack is £35 with no commission. An £89 Court Pack is planned but not currently available. Start with a free eligibility check."
        canonical="/pricing"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <FAQSchema faqs={faqsForSchema} />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-16 lg:py-24">
        {/* Top diagonal stripe bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Floating elements */}
        <div className="absolute top-40 left-10 w-24 h-24 bg-accent-500/10 rounded-full blur-2xl" />
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-brand-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-brand-600 mb-6">
            <Link to="/" className="hover:text-brand-700 transition-colors">Home</Link>
            <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-800">Pricing</span>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-success-500/10 rounded-full text-success-600 text-sm font-medium mb-6">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            No commission — keep 100% of your compensation
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-950 leading-tight mb-6">
            Simple, Honest{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-accent-600">
              Pricing
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-surface-800 max-w-2xl mx-auto">
            One flat fee. No percentage cuts. No hidden charges. 
            You only pay when you're ready to generate your claim pack.
          </p>
        </div>

        {/* Bottom diagonal stripe bar */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* Value Proposition Cards */}
      <section className="relative -mt-8 z-10 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {valueProps.map((prop, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 shadow-xl shadow-brand-900/5 border border-surface-200"
              >
                <div className="text-3xl mb-3">{prop.icon}</div>
                <h3 className="font-display font-semibold text-brand-950 mb-2">{prop.title}</h3>
                <p className="text-sm text-surface-800">{prop.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-brand-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              CHOOSE YOUR PACK
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 mb-4">
              Pay Only For What You Need
            </h2>
            <p className="text-brand-300 text-lg max-w-2xl mx-auto">
              Start free. The £35 Council Pack is the only paid product currently available.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Tier */}
            <div className="relative bg-brand-900/50 rounded-3xl p-8 border border-brand-800 flex flex-col">
              <div className="mb-6">
                <div className="w-12 h-12 bg-brand-800 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-brand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-display text-2xl font-semibold text-white mb-2">
                  Eligibility Check
                </h3>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="font-display text-5xl font-bold text-white">Free</span>
                </div>
                <p className="text-brand-300">
                  Find out if your claim meets common eligibility criteria before investing any time
                </p>
              </div>

              <div className="flex-grow">
                <div className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">
                  What's included
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    'Quick indication on whether it\'s worth proceeding',
                    'Initial claim assessment',
                    'Responsible authority lookup',
                    'Understand what evidence councils typically expect',
                    'Evidence requirements checklist',
                    'Avoid spending time gathering evidence unnecessarily',
                    'Basic guidance on next steps',
                    'No obligation to continue',
                    'No credit card required',
                  ].map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-brand-100">
                      <svg className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to="/check"
                className="block w-full text-center px-6 py-4 bg-brand-800 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
              >
                Start Free Check
              </Link>
            </div>

            {/* Council Pack - Featured */}
            <div className="relative bg-white rounded-3xl p-8 border-2 border-accent-500 shadow-2xl shadow-accent-500/20 flex flex-col lg:-mt-4 lg:mb-[-1rem]">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 bg-accent-500 text-white text-sm font-bold rounded-full shadow-lg">
                AVAILABLE NOW
              </div>
              
              <div className="mb-6">
                <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="font-display text-2xl font-semibold text-brand-950 mb-2">
                  Council Claim Pack
                </h3>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="font-display text-5xl font-bold text-brand-950">£35</span>
                  <span className="text-surface-800">one-time</span>
                </div>
                <p className="text-surface-800">
                  Everything you need to submit a professional claim to your council
                </p>
              </div>

              <div className="flex-grow">
                <div className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">
                  What's included
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    'Professionally formatted claim pack (citing the Highways Act)',
                    'Council-specific submission guide',
                    'Organised evidence bundle',
                    'AI-powered evidence analysis',
                    'Prior reports check (FixMyStreet)',
                    'Know Your Rights briefing (general guidance)',
                    'Follow-up letter templates',
                    'Clear audit trail of your claim submission',
                    'Designed to match typical council claim requirements',
                  ].map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-surface-800">
                      <svg className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to="/check"
                className="block w-full text-center px-6 py-4 bg-accent-500 text-white font-bold rounded-xl hover:bg-accent-600 transition-colors shadow-lg shadow-accent-500/25"
              >
                Get Started — £35
              </Link>
            </div>

            {/* Planned Court Pack */}
            <div className="relative bg-brand-900/50 rounded-3xl p-8 border border-brand-800 flex flex-col">
              <div className="absolute -top-3 right-6 px-3 py-1 bg-brand-700 text-brand-200 text-xs font-medium rounded-full">
                PLANNED
              </div>
              
              <div className="mb-6">
                <div className="w-12 h-12 bg-brand-800 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-brand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
                <h3 className="font-display text-2xl font-semibold text-white mb-2">
                  Court Claim Pack
                </h3>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="font-display text-5xl font-bold text-white">£89</span>
                  <span className="text-brand-300">one-time</span>
                </div>
                <p className="text-brand-300">
                  Prepare to escalate to small claims court if your council rejects your claim
                </p>
              </div>

              <div className="flex-grow">
                <div className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">
                  What's included
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    'Pre-filled N1 court claim form',
                    'Particulars of claim template',
                    'Court-ready evidence bundle',
                    'Complete court process guide',
                    'What to expect at a small claims hearing',
                    'Hearing preparation checklist',
                    'Plain-English explanation of court paperwork and deadlines',
                    'Designed for self-representation in small claims court',
                    'Helps you present your case clearly and calmly',
                    'No solicitor required',
                  ].map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-brand-100">
                      <svg className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <button
                disabled
                className="block w-full text-center px-6 py-4 bg-brand-800 text-brand-400 font-semibold rounded-xl cursor-not-allowed"
              >
                Planned — not currently available
              </button>
            </div>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-8 mt-12 text-brand-300 text-sm">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Secure payment
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              No percentage fees
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              All major cards accepted
            </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              FEATURE COMPARISON
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-950 mt-3 mb-4">
              What's In Each Pack
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-brand-100">
                  <th className="text-left py-4 pr-4 font-display font-semibold text-brand-950">Feature</th>
                  <th className="text-center py-4 px-4 font-display font-semibold text-brand-600">Free</th>
                  <th className="text-center py-4 px-4 font-display font-semibold text-accent-600">Council £35</th>
                  <th className="text-center py-4 px-4 font-display font-semibold text-brand-600">Planned Court £89</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className="border-b border-surface-200">
                    <td className="py-5 pr-4">
                      <div className="font-medium text-surface-900">{row.feature}</div>
                      <div className="text-sm text-surface-600 mt-1">{row.description}</div>
                    </td>
                    <td className="text-center py-5 px-4">
                      {row.free ? (
                        <svg className="w-6 h-6 text-success-500 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-surface-300 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                    </td>
                    <td className="text-center py-5 px-4">
                      {row.council ? (
                        <svg className="w-6 h-6 text-success-500 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-surface-300 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                    </td>
                    <td className="text-center py-5 px-4">
                      {row.court ? (
                        <svg className="w-6 h-6 text-success-500 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-surface-300 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Why Not Alternatives */}
      <section className="py-20 bg-gradient-to-br from-brand-50 via-white to-accent-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              COMPARE YOUR OPTIONS
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-950 mt-3 mb-4">
              Why Choose Fixtyer?
            </h2>
            <p className="text-surface-800 text-lg max-w-2xl mx-auto">
              You have options. Here's how they compare.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {alternatives.map((alt, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border-2 border-surface-200 shadow-lg"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display font-semibold text-brand-950">{alt.option}</h3>
                  <span className="text-error-500 text-xs font-medium bg-error-500/10 px-2 py-1 rounded-full">
                    {alt.cost}
                  </span>
                </div>
                <div className="text-sm text-surface-800 mb-4">
                  <span className="text-brand-600 font-medium">Time:</span> {alt.time}
                </div>
                <ul className="space-y-2">
                  {alt.issues.map((issue, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-surface-800">
                      <svg className="w-4 h-4 text-error-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Fixtyer comparison */}
          <div className="bg-gradient-to-br from-accent-500 to-accent-600 rounded-3xl p-8 md:p-10 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl" />
            
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M6 18L12 6L18 18" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="12" cy="14" r="2" fill="currentColor" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-display text-2xl font-bold">Fixtyer</h3>
                  <p className="text-white/80">The smart choice</p>
                </div>
              </div>

              {/* Value cards on blue background */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                <div className="bg-brand-900/80 rounded-xl p-4 backdrop-blur-sm border border-brand-700/50">
                  <div className="text-3xl font-display font-bold mb-1 text-white">£35</div>
                  <p className="text-brand-200 text-sm">One-time flat fee</p>
                </div>
                <div className="bg-brand-900/80 rounded-xl p-4 backdrop-blur-sm border border-brand-700/50">
                  <div className="text-3xl font-display font-bold mb-1 text-white">100%</div>
                  <p className="text-brand-200 text-sm">You keep all compensation</p>
                </div>
                <div className="bg-brand-900/80 rounded-xl p-4 backdrop-blur-sm border border-brand-700/50">
                  <div className="text-3xl font-display font-bold mb-1 text-white">Online</div>
                  <p className="text-brand-200 text-sm">Prepare at your own pace</p>
                </div>
                <div className="bg-brand-900/80 rounded-xl p-4 backdrop-blur-sm border border-brand-700/50">
                  <div className="text-3xl font-display font-bold mb-1 text-white">Structured</div>
                  <p className="text-brand-200 text-sm">Documents for your review</p>
                </div>
              </div>

              {/* Benefit chips - more prominent */}
              <div className="flex flex-wrap gap-3 mt-8">
                {[
                  'Professional claim letter',
                  'Prior reports checked',
                  'Evidence analysis',
                  'Council-specific guides',
                  'You stay in control',
                  'No commission ever',
                ].map((benefit) => (
                  <div key={benefit} className="flex items-center gap-2 bg-white px-4 py-2 rounded-full text-sm font-medium text-brand-900 shadow-lg">
                    <svg className="w-4 h-4 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {benefit}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Math */}
      <section className="py-20 bg-brand-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              THE NUMBERS
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 mb-4">
              Let's Do The Maths
            </h2>
          </div>

          <div className="bg-brand-900/50 rounded-3xl p-8 border border-brand-800">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-white font-display font-semibold text-lg mb-4">Typical pothole damage</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-brand-200">
                    <span>Tyre replacement</span>
                    <span className="font-mono">£80 - £200</span>
                  </div>
                  <div className="flex justify-between text-brand-200">
                    <span>Alloy wheel repair</span>
                    <span className="font-mono">£50 - £100</span>
                  </div>
                  <div className="flex justify-between text-brand-200">
                    <span>Alloy wheel replacement</span>
                    <span className="font-mono">£150 - £400</span>
                  </div>
                  <div className="flex justify-between text-brand-200">
                    <span>Suspension damage</span>
                    <span className="font-mono">£200 - £500</span>
                  </div>
                  <div className="flex justify-between text-brand-200">
                    <span>Tracking/alignment</span>
                    <span className="font-mono">£40 - £80</span>
                  </div>
                </div>
              </div>

              <div className="bg-brand-800/50 rounded-2xl p-6">
                <h3 className="text-white font-display font-semibold text-lg mb-4">Example scenario</h3>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-brand-200">
                    <span>Your repair bill</span>
                    <span className="font-mono text-white">£300</span>
                  </div>
                  <div className="flex justify-between text-brand-200">
                    <span>Fixtyer Council Pack</span>
                    <span className="font-mono text-accent-400">-£35</span>
                  </div>
                  <div className="border-t border-brand-700 pt-3 flex justify-between items-center">
                    <span className="text-white font-semibold">You keep</span>
                    <span className="font-mono text-2xl text-white font-bold">£265</span>
                  </div>
                </div>

                <div className="text-sm text-brand-300">
                  <span className="text-accent-400 font-medium">Compare:</span> A claims company at 30% would take £90, leaving you with just £210.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              QUESTIONS
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-950 mt-3 mb-4">
              Pricing FAQs
            </h2>
          </div>

          <div className="space-y-4">
            {pricingFaqs.map((faq, index) => (
              <div 
                key={index}
                className={clsx(
                  'rounded-2xl border-2 transition-all duration-300',
                  openFaq === index 
                    ? 'border-brand-200 bg-brand-50/50 shadow-lg' 
                    : 'border-surface-200 bg-white hover:border-brand-100'
                )}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <span className={clsx(
                    'font-display font-semibold transition-colors',
                    openFaq === index ? 'text-brand-900' : 'text-brand-950'
                  )}>
                    {faq.question}
                  </span>
                  <div className={clsx(
                    'w-8 h-8 rounded-lg flex items-center justify-center transition-all flex-shrink-0 ml-4',
                    openFaq === index 
                      ? 'bg-brand-600 rotate-180' 
                      : 'bg-surface-100'
                  )}>
                    <svg 
                      className={clsx('w-4 h-4 transition-colors', openFaq === index ? 'text-white' : 'text-surface-800')} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                <div className={clsx(
                  'overflow-hidden transition-all duration-300',
                  openFaq === index ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                )}>
                  <div className="px-6 pb-6 text-surface-800">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12">
        <CouncilPackProof />
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-brand-600 to-brand-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/90 text-sm font-medium mb-6">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Start free — only pay when ready
          </div>
          
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            £35 to prepare a professional claim
          </h2>
          <p className="text-xl text-brand-100 mb-8 max-w-2xl mx-auto">
            Prepare the documents and evidence you need to submit and manage your own claim.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-brand-700 font-semibold rounded-xl hover:bg-brand-50 transition-all hover:shadow-lg"
            >
              Start Your Free Check
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/how-it-works"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-semibold rounded-xl border-2 border-white/30 hover:bg-white/10 transition-colors"
            >
              See How It Works
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom stripe */}
      <div 
        className="h-8"
        style={{
          background: `repeating-linear-gradient(
            -45deg,
            #1e40af,
            #1e40af 40px,
            #f97316 40px,
            #f97316 80px
          )`
        }}
      />
    </div>
  );
}
